<template>
  <div
    class="bg-white dark:bg-gray-800 sm:rounded-lg shadow dark:shadow-gray-900/50 overflow-hidden"
  >
    <!-- Accordion Header (always visible, clickable) -->
    <div class="flex items-center">
      <div
        role="button"
        tabindex="0"
        @click="$emit('toggle')"
        @keydown.enter.prevent="$emit('toggle')"
        @keydown.space.prevent="$emit('toggle')"
        class="flex-1 px-3 sm:px-4 py-2.5 sm:py-3 flex items-center justify-between transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500"
        :class="
          isNoDeal
            ? 'bg-red-50 dark:bg-red-900/30 hover:bg-red-100 dark:hover:bg-red-900/50'
            : isDone
              ? 'bg-green-50 dark:bg-green-900/30 hover:bg-green-100 dark:hover:bg-green-900/50'
              : 'hover:bg-gray-50 dark:hover:bg-gray-700'
        "
      >
        <div class="flex items-center gap-4 flex-1 min-w-0">
          <!-- Status indicator - fixed width to prevent layout shift -->
          <div class="flex-shrink-0 w-10 h-6 flex items-center justify-center">
            <XCircleIcon v-if="isNoDeal" class="h-6 w-6 text-red-500" />
            <CheckCircleIcon v-else-if="isDone" class="h-6 w-6 text-green-500" />
            <div v-else-if="isLoadingPreferences">
              <div
                class="animate-spin rounded-full h-5 w-5 border-2 border-gray-300 border-t-blue-600"
              ></div>
            </div>
            <div
              v-else
              class="text-xs text-gray-600 dark:text-gray-400 font-medium whitespace-nowrap"
            >
              {{ assignedCount }} / {{ roleCount }}
            </div>
          </div>

          <!-- Organization & Subject -->
          <div class="flex-1 min-w-0 text-left">
            <div class="flex items-center gap-2">
              <span
                class="font-medium text-gray-900 dark:text-white truncate select-text cursor-text"
              >
                {{ prospect.organization_name || 'Onbekend' }}
              </span>
              <span
                v-if="prospect.is_top20"
                class="flex-shrink-0 px-2 py-0.5 text-xs font-semibold rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-300"
              >
                Top 20
              </span>
              <!-- Workflow status badge (hidden when No Deal badge is visible) -->
              <template v-if="!isNoDeal">
                <span
                  v-if="prospect.workflow_status === 'goedgekeurd'"
                  class="flex-shrink-0 px-2 py-0.5 text-xs font-semibold rounded-full bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300"
                >
                  Gematched
                </span>
                <span
                  v-else-if="prospect.workflow_status === 'te_bespreken'"
                  class="flex-shrink-0 px-2 py-0.5 text-xs font-semibold rounded-full bg-orange-100 text-orange-800 dark:bg-orange-900/50 dark:text-orange-300"
                >
                  Te Bespreken
                </span>
                <span
                  v-else-if="
                    prospect.workflow_status === 'afgekeurd' && !prospect.sourcing_rejected_at
                  "
                  class="flex-shrink-0 px-2 py-0.5 text-xs font-semibold rounded-full bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300"
                >
                  Geen Match
                </span>
                <span
                  v-else-if="
                    prospect.workflow_status === 'afgekeurd' && prospect.sourcing_rejected_at
                  "
                  class="flex-shrink-0 px-2 py-0.5 text-xs font-semibold rounded-full bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400"
                >
                  Afgekeurd
                </span>
              </template>
              <!-- PL feedback icon -->
              <ChatBubbleLeftEllipsisIcon
                v-if="prospect.review_feedback"
                class="h-4 w-4 flex-shrink-0"
                :class="
                  prospect.workflow_status === 'te_bespreken' ||
                  prospect.workflow_status === 'afgekeurd'
                    ? 'text-orange-500 dark:text-orange-400'
                    : 'text-blue-500 dark:text-blue-400'
                "
                :title="`Feedback van ${prospect.reviewed_by_name || 'Performance Lead'}`"
              />
            </div>
            <div class="text-sm text-gray-500 dark:text-gray-400 truncate select-text cursor-text">
              {{ prospect.subject }}
            </div>
            <div
              v-if="isDone && !isExpanded && employeeNames.length > 0"
              class="text-xs text-green-600 dark:text-green-400 truncate"
            >
              {{ employeeNames.join(', ') }}
            </div>
          </div>

          <!-- Quick info (visible on larger screens) -->
          <div class="hidden md:flex items-center gap-6 text-sm text-gray-500 dark:text-gray-400">
            <!-- No deal badge -->
            <div
              v-if="
                prospect.action_type_code === 'kans_afwijzen' &&
                prospect.action_status !== 'deleted' &&
                prospect.action_status !== 'cancelled'
              "
              class="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-600 dark:bg-red-900/50 dark:text-red-400"
            >
              <CheckIcon v-if="prospect.action_status === 'completed'" class="h-3.5 w-3.5" />
              <XMarkIcon v-else class="h-3.5 w-3.5" />
              No deal
            </div>
            <!-- Deal badge -->
            <div
              v-else-if="
                prospect.action_type_code === 'project_maken' &&
                prospect.action_status !== 'deleted' &&
                prospect.action_status !== 'cancelled'
              "
              class="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300"
            >
              <CheckIcon class="h-3.5 w-3.5" />
              Deal
            </div>
            <!-- Ontbrekende actie badge -->
            <div
              v-else-if="
                prospect.action_status === 'deleted' ||
                prospect.action_status === 'cancelled' ||
                (isDone && !prospect.action_status)
              "
              class="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-600 dark:bg-red-900/50 dark:text-red-400"
            >
              Ontbrekende actie
            </div>
            <!-- Action type badge with checkmark for completed -->
            <div
              v-else-if="prospect.action_type_name"
              class="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
              :class="actionBadgeClass"
            >
              <CheckIcon v-if="prospect.action_status === 'completed'" class="h-3.5 w-3.5" />
              {{ prospect.action_type_name }}
            </div>
            <!-- Actiehouder(s) alleen tonen op Doen pagina, niet hier -->
            <div class="flex items-center gap-1.5">
              <span class="font-medium text-gray-900 dark:text-white"
                >&euro;{{ formatNumber(prospect.expected_revenue) }}</span
              >
              <span class="text-xs">({{ prospect.chance_to_score ?? '-' }}%)</span>
              <span
                v-if="prospect.ml_prediction"
                class="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-xs font-medium"
                :class="mlBadgeClass"
                :title="`ML voorspelling: ${Math.round(prospect.ml_prediction.predicted_probability * 100)}%`"
              >
                <SparklesIcon class="h-3 w-3" />
                {{ Math.round(prospect.ml_prediction.predicted_probability * 100) }}%
              </span>
            </div>
            <div>
              {{ formatDate(prospect.start_date) }}
            </div>
            <div class="flex items-center gap-1 min-w-[70px]">
              <UserIcon class="h-4 w-4" />
              <span>{{ roleCount }} {{ roleCount === 1 ? 'rol' : 'rollen' }}</span>
            </div>
          </div>
        </div>

        <!-- Expand/collapse icon -->
        <ChevronDownIcon
          class="h-5 w-5 text-gray-400 dark:text-gray-500 transition-transform flex-shrink-0 ml-4"
          :class="isExpanded ? 'rotate-180' : ''"
        />
      </div>

      <!-- Reassess button -->
      <button
        @click="handleReassess"
        class="px-3 py-3 text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/30 hover:text-amber-700 dark:hover:text-amber-300 transition-colors border-l border-gray-200 dark:border-gray-700"
        title="Opnieuw beoordelen"
      >
        <ArrowUturnLeftIcon class="h-5 w-5" />
      </button>

      <!-- Reject button -->
      <button
        v-if="
          !isDone && !(prospect.workflow_status === 'afgekeurd' && prospect.sourcing_rejected_at)
        "
        @click="handleReject"
        class="px-3 py-3 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 hover:text-red-700 dark:hover:text-red-300 transition-colors border-l border-gray-200 dark:border-gray-700"
        title="Afkeuren"
      >
        <XCircleIcon class="h-5 w-5" />
      </button>

      <!-- Action button (only when no active action exists, not for no deal) -->
      <button
        v-if="
          !isNoDeal &&
          ((isFullyAssigned && !prospect.action_status) ||
            prospect.action_status === 'deleted' ||
            prospect.action_status === 'cancelled')
        "
        @click="$emit('action', prospect)"
        class="px-3 py-3 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 hover:text-blue-700 dark:hover:text-blue-300 transition-colors border-l border-gray-200 dark:border-gray-700"
        title="Actie toewijzen"
      >
        <PlusIcon class="h-5 w-5" />
      </button>
    </div>

    <!-- Accordion Content (collapsible) -->
    <div v-show="isExpanded" class="border-t border-gray-100 dark:border-gray-700">
      <slot></slot>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import {
  CheckCircleIcon,
  CheckIcon,
  ChevronDownIcon,
  UserIcon,
  XCircleIcon,
  XMarkIcon,
  PlusIcon,
  SparklesIcon,
  ArrowUturnLeftIcon,
  ChatBubbleLeftEllipsisIcon
} from '@heroicons/vue/24/outline'

const props = defineProps({
  prospect: {
    type: Object,
    required: true
  },
  isExpanded: {
    type: Boolean,
    default: false
  },
  isFullyAssigned: {
    type: Boolean,
    default: false
  },
  isDone: {
    type: Boolean,
    default: false
  },
  assignedCount: {
    type: Number,
    default: 0
  },
  roleCount: {
    type: Number,
    default: 0
  },
  isLoadingPreferences: {
    type: Boolean,
    default: false
  },
  employeeNames: {
    type: Array,
    default: () => []
  }
})

const emit = defineEmits(['toggle', 'reject', 'reassess', 'action'])

// Check if prospect has an active no deal outcome (kans_afwijzen follow-up).
// Verwijderde of geannuleerde acties tellen niet — anders blijft de "Actie
// toewijzen"-knop verborgen nadat een kans_afwijzen is teruggedraaid.
const isNoDeal = computed(
  () =>
    props.prospect.action_type_code === 'kans_afwijzen' &&
    props.prospect.action_status !== 'deleted' &&
    props.prospect.action_status !== 'cancelled'
)

// Compute ML prediction badge color
const mlBadgeClass = computed(() => {
  const prob = props.prospect.ml_prediction?.predicted_probability
  if (prob == null) return ''
  if (prob >= 0.7) return 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300'
  if (prob >= 0.4) return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-300'
  return 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300'
})

// Compute badge class based on action status
const actionBadgeClass = computed(() => {
  const status = props.prospect.action_status
  if (status === 'completed') {
    return 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300'
  } else if (status === 'cancelled') {
    return 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
  } else if (status === 'deleted') {
    return 'bg-red-100 text-red-600 dark:bg-red-900/50 dark:text-red-400'
  }
  // Default: open
  return 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300'
})

function formatDate(dateString) {
  if (!dateString) return '-'
  return new Date(dateString).toLocaleDateString('nl-NL', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  })
}

function formatNumber(num) {
  if (!num) return '0'
  return new Intl.NumberFormat('nl-NL').format(num)
}

function handleReject(event) {
  emit('reject', props.prospect, event)
}

function handleReassess(event) {
  emit('reassess', props.prospect, event)
}
</script>
